import { useState, useEffect } from "react";
import moment from "moment";

import { Button, Checkbox, Form, Modal, Radio, Select, Typography } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { debounce } from "lodash";

import { ErrorResponse } from "utils/types";
import notifier from "utils/notifier";
import { ClickEvents, HealthCodes } from "utils/enums";
import { addCustomAppInsightsEvent, formatDate } from "utils/helpers";

import { GetEddDateResponse } from "store/eligibility/edd/actions";
import { GetLmpDateResponse } from "store/eligibility/lmp/actions";

import FloatLabel from "components/Common/FloatLabel";
import DatePickerLocalised from "components/Common/DatePickerLocalised";
import MedicareHCCSection from "components/Eligibility/MemberEligibilityDetailTables/MedicareHCCSection";

import HealthAssessmentTabProps from "./Proptypes";

const { Title } = Typography;
const { Option } = Select;

const HealthAssessmentTab = (props: HealthAssessmentTabProps) => {
  const healthAssessmentOptions = [
    HealthCodes.NEW_PATIENT,
    HealthCodes.DIABETES,
    HealthCodes.HYPERTENSION,
    HealthCodes.SMOKER,
  ];
  //only females have pregnancy
  if (props.eligibilityMemberInfo.RmiSex === "F")
    healthAssessmentOptions.push(HealthCodes.PREGNANT);

  const [form] = Form.useForm();
  const [portalLanguage, setPortalLanguage] = useState("English");
  const [lmpDate, setLmpDate] = useState("");
  const [eddDate, setEddDate] = useState("");
  const [updatedEddDate, setUpdatedEddDate] = useState(true);
  const [updatedLmpDate, setUpdatedLmpDate] = useState(true);
  const [healthCheckList, setHealthCheckList] = useState<string[]>([]);
  const [isDisabled, setIsDisabled] = useState(true); //Disables Date Picker by default
  const languages = ["English", "Spanish", "Chinese"];

  useEffect(() => {
    if (props.activeTab === "1") {
      initHealthAssessment();
      getEddDate();
      getLmpDate();
    }
  }, [props.activeTab]);

  //wait for date picker state to update before saving and don't save on page refresh
  useEffect(() => {
    if (!updatedLmpDate) saveLmpDate();
  }, [lmpDate, updatedLmpDate]);

  //wait for date picker state to update before saving and don't save on page refresh
  useEffect(() => {
    if (!updatedEddDate) saveEddDate();
  }, [eddDate, updatedEddDate]);

  const getEddDate = () => {
    return props
      .getEddDate(
        props.eligibilityMemberInfo.RmiCompanyId,
        props.eligibilityMemberInfo.RmiMembMpiNo
      )
      .then((response: GetEddDateResponse) => {
        setEddDate(formatDate(response?.RmiEstDueDate, "MM/DD/YYYY"));
        setUpdatedEddDate(true);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const saveEddDate = () => {
    const eddDateParams = {
      RmiCompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      RmiEddDate: eddDate,
      RmiMembMpiNo: props.eligibilityMemberInfo.RmiMembMpiNo,
      RmiProvKeyId: props.eligibilityMemberInfo.RmiProvKeyId,
    };

    form.validateFields(["eddDate"]).then(() => {
      return props
        .saveEddDate(eddDateParams)
        .then(() => {
          notifier.success("HealthAssessment Updated.");
          setUpdatedEddDate(true);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(
            err.response?.data?.Message ||
              "Something went wrong, please contact a system administrator."
          );
        });
    });
  };

  const getLmpDate = () => {
    return props
      .getLmpDate(
        props.eligibilityMemberInfo.RmiCompanyId,
        props.eligibilityMemberInfo.RmiMembMpiNo
      )
      .then((response: GetLmpDateResponse) => {
        setLmpDate(formatDate(response?.RmiLmpDate, "MM/DD/YYYY"));
        setUpdatedLmpDate(true);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const saveLmpDate = () => {
    const lmpDateParams = {
      RmiCompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      RmiLmpDate: lmpDate,
      RmiMembMpiNo: props.eligibilityMemberInfo.RmiMembMpiNo,
      RmiProvKeyId: props.eligibilityMemberInfo.RmiProvKeyId,
    };

    form.validateFields(["lmpDate"]).then(() => {
      return props
        .saveLmpDate(lmpDateParams)
        .then(() => {
          notifier.success("HealthAssessment Updated.");
          setUpdatedLmpDate(true);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(
            err.response?.data?.Message ||
              "Something went wrong, please contact a system administrator."
          );
        });
    });
  };

  const saveHealth = (health: any) => {
    const diabetes =
      health.filter((value: string) => value === HealthCodes.DIABETES).length >
      0
        ? "Y"
        : "N";
    const hypertension =
      health.filter((value: string) => value === HealthCodes.HYPERTENSION)
        .length > 0
        ? "Y"
        : "N";
    const newPatient =
      health.filter((value: string) => value === HealthCodes.NEW_PATIENT)
        .length > 0
        ? "Y"
        : "N";
    const osteoporosis =
      health.filter((value: string) => value === HealthCodes.OSTEOPOROSIS)
        .length > 0
        ? "Y"
        : "N";
    const pregnant =
      health.filter((value: string) => value === HealthCodes.PREGNANT).length >
      0
        ? "Y"
        : "N";
    const rheumatoidArthritis =
      health.filter(
        (value: string) => value === HealthCodes.RHEUMATOID_ARTHRITIS
      ).length > 0
        ? "Y"
        : "N";
    const smoker =
      health.filter((value: string) => value === HealthCodes.SMOKER).length > 0
        ? "Y"
        : "N";
    if (pregnant === "Y") {
      setIsDisabled(false); //Enables the Date Picker is pregnant is checked
    } else {
      setIsDisabled(true); //Disables the Date Picker is pregnant is not checked
    }
    const healthParams = {
      Diabetes: diabetes,
      Hypertension: hypertension,
      Language: portalLanguage,
      Lmp: lmpDate,
      NewPatient: newPatient,
      Osteoporosis: osteoporosis,
      Pregnant: pregnant,
      RheumatoidArthritis: rheumatoidArthritis,
      Smoker: smoker,
    };

    const memberInfoParams = {
      CompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      MembMpiNo: props.eligibilityMemberInfo.RmiMembMpiNo,
    };

    setHealthCheckList(health);

    props
      .saveHealth(healthParams, memberInfoParams)
      .then(() => {
        notifier.success("saveHealth Success");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });

    saveEddDate();
    saveLmpDate();
  };

  const getPrintFile = () => {
    const params = {
      CompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      MembKeyId: props.eligibilityMemberInfo.RmiMembKeyId,
      MembMpiNo: props.eligibilityMemberInfo.RmiMembMpiNo,
      NewPatient: "N",
      Provider: props.primaryCareProvider.RmiRevFullName,
      material: [], //TODO not really sure what this material is
    };
    addCustomAppInsightsEvent(ClickEvents.PRINT_PAF, params);
    return props
      .getPrintFile(params)
      .then((data: any) => {
        const file = new Blob([data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const initHealthAssessment = () => {
    const defaultHealthAssessment: string[] = [];
    if (props.eligibilityMemberInfo.RmiNewPatient !== "N")
      defaultHealthAssessment.push(HealthCodes.NEW_PATIENT);
    if (props.eligibilityMemberInfo.RmiHdDiabeticFlag !== "N")
      defaultHealthAssessment.push(HealthCodes.DIABETES);
    if (props.eligibilityMemberInfo.RmiHdHighBPFlag !== "N")
      defaultHealthAssessment.push(HealthCodes.HYPERTENSION);
    if (props.eligibilityMemberInfo.RmiHdOsteoporosisFlag !== "N")
      defaultHealthAssessment.push(HealthCodes.OSTEOPOROSIS);
    if (props.eligibilityMemberInfo.RmiHdRaFlag !== "N")
      defaultHealthAssessment.push(HealthCodes.RHEUMATOID_ARTHRITIS);
    if (props.eligibilityMemberInfo.RmiHdSmokerFlag !== "N")
      defaultHealthAssessment.push(HealthCodes.SMOKER);
    if (
      props.eligibilityMemberInfo.RmiSex === "F" &&
      props.eligibilityMemberInfo.RmiHdPregnantFlag === "Y"
    ) {
      defaultHealthAssessment.push(HealthCodes.PREGNANT);
      setIsDisabled(false); //Enables the Date Picker is pregnant and is not checked
    }
    //theres are 3 languages from legacy code and currently defaults to English if RmiPortalLanguage is null
    setPortalLanguage(
      props.eligibilityMemberInfo.RmiPortalLanguage === null
        ? languages[0]
        : props.eligibilityMemberInfo.RmiPortalLanguage
    );
    setHealthCheckList(defaultHealthAssessment);
  };

  const updateEddDate = (date: string) => {
    setUpdatedEddDate(true);

    if (date != null && eddDate != date) {
      setUpdatedEddDate(false);
      setEddDate(date);
    }
  };

  const updateLmpDate = (date: string) => {
    setUpdatedLmpDate(true);

    if (date != null && lmpDate != date) {
      setUpdatedLmpDate(false);
      setLmpDate(date);
    }
  };

  const validationRules = async (fieldData: any, value: any) => {
    if (
      (fieldData.field === "lmpDate" || fieldData.field === "eddDate") &&
      value &&
      (!moment(value).isValid() || moment(value).year() < 1900 || !value.match(/^\d{2}\/\d{2}\/\d{4}$/))
    ) {
      return Promise.reject(new Error("Invalid Date"));
    }

    return Promise.resolve();
  };

  return (
    <>
      <div className="measure-year" style={{ width: "8%", float: "right" }}>
        <div className="row custom-mt">
          <div className="col-lg-24 col-md-24 col-sm-24">
            <FloatLabel label="Measure Year" value={props.measureYear}>
              <Select
                className="mb-2"
                showSearch
                value={props.measureYear}
                placeholder="ALL"
                onChange={(value) => props.setMeasureYear(value)}
              >
                <Option key={1} value={moment().year()}>
                  {moment().year()}
                </Option>
                <Option key={2} value={moment().year() - 1}>
                  {moment().year() - 1}
                </Option>
              </Select>
            </FloatLabel>
          </div>
        </div>
      </div>

      <MedicareHCCSection
        eligibilityMemberInfo={props.eligibilityMemberInfo}
        getDetails={props.getDetails}
        getDiags={props.getDiags}
        updateStatusAndComment={props.updateStatusAndComment}
        activeKey={props.activeTab}
        isFetchingDiags={props.isFetchingDiags}
        isFetchingDetails={props.isFetchingDetails}
        measureYear={props.measureYear}
      />

      <div className="health-assessment-section">
        <Form
          form={form}
          name="pregnant-form"
          onFinish={() => {
            saveLmpDate();
            saveEddDate();
          }}
        >
          <div className="row">
            <div className="col">
              <Title className="ml-3" level={5}>
                Health Assessment (Check all that apply:)
              </Title>
            </div>
            <div className="col">
              <div style={{ textAlign: "right" }}>
                <b>Preferred Language: </b>
                <Select
                  value={portalLanguage}
                  onChange={(value) => setPortalLanguage(value)}
                >
                  <Option value="English">English</Option>
                  <Option value="Spanish">Spanish</Option>
                  <Option value="Chinese">Chinese</Option>
                </Select>
              </div>
            </div>
            <Button
              className="float-right mr-3"
              type="primary"
              onClick={getPrintFile}
              icon={<PrinterOutlined />}
            >
              Print Assessment Form
            </Button>
          </div>

          <div className="row">
            <div className="col-lg-12 col-md-12">
              <Checkbox.Group
                className="ml-3 mt-4"
                options={healthAssessmentOptions}
                value={healthCheckList}
                onChange={(onChange) => saveHealth(onChange)}
              />
            </div>

            {props.eligibilityMemberInfo.RmiSex === "F" && !isDisabled && (
              <>
                <div className="col-lg-6 col-md-6 mt-3">
                  <FloatLabel
                    name="lmpDate"
                    label="Last Menstrual Date (MM/DD/YYYY)"
                    value={lmpDate}
                    form={form}
                    rules={[
                      () => ({
                        validator: validationRules,
                      }),
                    ]}
                  >
                    <DatePickerLocalised
                      date={lmpDate}
                      setDate={updateLmpDate}
                      formInstance={form}
                      formItemName={"lmpDate"}
                      disabled={isDisabled}
                    />
                  </FloatLabel>
                </div>
                <div className="col-lg-6 col-md-6 mt-3">
                  <FloatLabel
                    name="eddDate"
                    label="Estimated Due Date (MM/DD/YYYY)"
                    value={eddDate}
                    form={form}
                    rules={[
                      () => ({
                        validator: validationRules,
                      }),
                    ]}
                  >
                    <DatePickerLocalised
                      date={eddDate}
                      setDate={updateEddDate}
                      formInstance={form}
                      formItemName={"eddDate"}
                      disabled={isDisabled}
                    />
                  </FloatLabel>
                </div>
              </>
            )}
          </div>
        </Form>
      </div>
    </>
  );
};

export default HealthAssessmentTab;
